import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";

/* ------------------ Types ------------------ */
type Gender = "Male" | "Female" | "Other" | "";

interface ContactForm {
  id?: number;
  salutation?: string;
  name: string;
  gender: Gender;
  phone: string;
  dob?: string;
  approx_age?: number | string | null;
  approx_age_indicator?: boolean;
  email?: string;
  address?: string;
  type: "EMPLOYEE" | "PATIENT";
}

const calculateAge = (dob?: string) => {
  if (!dob) return "";
  const date = new Date(dob);
  if (Number.isNaN(date.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) age--;
  return age >= 0 ? String(age) : "";
};

/* ------------------ Component ------------------ */
const AddEditContact: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const contactData: ContactForm | undefined = (location.state as any)?.contact;
  const isUpdate = Boolean(contactData?.id);
  const isView = location.state?.view === true;

  const [loading, setLoading] = useState(false);

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    redirectTo: undefined as string | undefined,
  });

  const [contact, setContact] = useState<ContactForm>({
    salutation: "",
    name: "",
    gender: "",
    phone: "",
    dob: "",
    approx_age: "",
    approx_age_indicator: false,
    email: "",
    address: "",
    type: "PATIENT",
  });

  /* ------------------ Prefill ------------------ */
  useEffect(() => {
    if (contactData) {
      setContact({
        ...contactData,
        salutation: contactData.salutation || "",
        dob: contactData.approx_age_indicator ? "" : contactData.dob || "",
        approx_age: contactData.approx_age ?? (contactData.approx_age_indicator ? calculateAge(contactData.dob) : ""),
      });
    }
  }, [contactData]);

  /* ------------------ Handlers ------------------ */
  const handleChange = (k: keyof ContactForm, v: any) =>
    setContact((p) => ({ ...p, [k]: v }));

  /* ------------------ Submit ------------------ */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isView) return;
    if (!contact.dob && !String(contact.approx_age ?? "").trim()) {
      setModal({
        open: true,
        title: "Missing age",
        message: "Enter either date of birth or approximate age.",
        redirectTo: undefined,
      });
      return;
    }

    setLoading(true);
    try {
      const url = isUpdate
        ? `${BACKEND_URL}/api/contacts/${contact.id}`
        : `${BACKEND_URL}/api/contacts/`;

      const method = isUpdate ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...contact,
          type: "PATIENT",
          approx_age: String(contact.approx_age ?? "").trim() ? Number(contact.approx_age) : null,
          approx_age_indicator: !contact.dob && Boolean(String(contact.approx_age ?? "").trim()),
        }),
      });

      if (!res.ok) throw new Error();

      setModal({
        open: true,
        title: "Success",
        message: `Contact ${isUpdate ? "updated" : "created"} successfully`,
        redirectTo: "/contacts",
      });
    } catch (err) {
      console.error(err);
      setModal({
        open: true,
        title: "Error",
        message: "Failed to save contact",
        redirectTo: undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <SidebarLayout
      title={isView ? "View Contact" : isUpdate ? "Edit Contact" : "Add Contact"}
    >
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          const r = modal.redirectTo;
          setModal({ ...modal, open: false });
          if (r) navigate(r);
        }}
      />

      <div className="bg-surface-light p-6 rounded border max-w-5xl">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <h3 className="font-semibold text-lg">Contact Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-[160px_minmax(0,1fr)_180px] gap-4">
            <select
              disabled={isView}
              value={contact.salutation || ""}
              onChange={(e) => handleChange("salutation", e.target.value)}
              className="p-3 border rounded"
            >
              <option value="">Salutation</option>
              <option value="Mr.">Mr.</option>
              <option value="Ms.">Ms.</option>
              <option value="Mrs.">Mrs.</option>
              <option value="Dr.">Dr.</option>
              <option value="Master">Master</option>
              <option value="Baby">Baby</option>
            </select>

            <input
              placeholder="Name"
              disabled={isView}
              value={contact.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="p-3 border rounded"
              required
            />

            <select
              disabled={isView}
              value={contact.gender}
              onChange={(e) => handleChange("gender", e.target.value)}
              className="p-3 border rounded"
            >
              <option value="">Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              placeholder="Phone"
              disabled={isView}
              value={contact.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="p-3 border rounded"
              required
            />

            <input
              type="date"
              aria-label="Date of birth"
              required={!String(contact.approx_age ?? "").trim()}
              disabled={isView}
              value={contact.dob}
              onChange={(e) => handleChange("dob", e.target.value)}
              className="p-3 border rounded"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="number"
              min="0"
              max="130"
              placeholder="Age"
              aria-label="Approximate age"
              required={!contact.dob}
              disabled={isView}
              value={contact.approx_age ?? ""}
              onChange={(e) => handleChange("approx_age", e.target.value)}
              className="p-3 border rounded"
            />

            <input
              type="email"
              placeholder="Email"
              disabled={isView}
              value={contact.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="p-3 border rounded"
            />
          </div>

          {!isView && <p className="text-sm text-subtext-light">Enter either date of birth or approximate age (required).</p>}

          <input
            placeholder="Address"
            disabled={isView}
            value={contact.address}
            onChange={(e) => handleChange("address", e.target.value)}
            className="p-3 border rounded"
          />

          <input
            value="Patient"
            disabled
            className="p-3 border rounded bg-background-light text-subtext-light"
          />

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate("/contacts")}
              className="px-4 py-2 rounded bg-background-light"
            >
              Back
            </button>

            {!isView && (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 rounded bg-primary text-white"
              >
                {loading ? "Saving..." : isUpdate ? "Update" : "Save"}
              </button>
            )}
          </div>
        </form>
      </div>
    </SidebarLayout>
  );
};

export default AddEditContact;
